import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import {
    enrichLensConfigWithUpdatedAt,
    parseCustomLensConfigFromApi,
} from 'components/molecules/PPA/Modals/CustomLense/customLens.helpers';
import type { CustomLensParsedConfig } from 'components/molecules/PPA/Modals/CustomLense/customLens.types';
import { parseLensConfigResponse } from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLensCatalog.helpers';
import type { LensCatalogFetchResult } from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLensCatalog.types';
import { ppaUrls } from 'config/urls';

export type LensConfigFetchResult = LensCatalogFetchResult & {
    parsedConfig: CustomLensParsedConfig | null;
};

export async function fetchLensConfig(lensId: number): Promise<LensConfigFetchResult> {
    const response = await axios.get(ppaUrls.getLensConfigUrl, {
        params: {
            lens_id: lensId,
        },
    });

    const raw = await enrichLensConfigWithUpdatedAt(response.data, lensId);

    return {
        lensId,
        catalog: parseLensConfigResponse(raw),
        parsedConfig: parseCustomLensConfigFromApi(raw),
        raw,
    };
}

export function useFetchLensConfig(lensId: number | null, enabled = true) {
    return useQuery({
        queryKey: ['ppa-lens-config', lensId],
        queryFn: async (): Promise<LensConfigFetchResult> => {
            if (lensId == null) {
                const response = await axios.get(ppaUrls.getLensConfigUrl, {
                    params: {
                        lens_id: lensId,
                    },
                });

                return {
                    lensId,
                    catalog: parseLensConfigResponse(response.data),
                    parsedConfig: parseCustomLensConfigFromApi(response.data),
                    raw: response.data,
                };
            }

            return fetchLensConfig(lensId);
        },
        enabled,
    });
}

export default useFetchLensConfig;
